import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Globe2, Euro } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

export default function AdminTransfers() {
  const { t: tt } = useLanguage();
  const [items, setItems] = useState([]);
  const [statut, setStatut] = useState('all');
  const [type, setType] = useState('all');
  const [devise, setDevise] = useState('all');

  const load = async () => {
    const params = statut !== 'all' ? { statut } : {};
    const r = await api.get('/admin/transfers', { params });
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statut]);

  const filtered = useMemo(() => {
    return items.filter((t) => {
      if (type !== 'all' && (t.type_virement || 'sepa') !== type) return false;
      if (devise !== 'all' && (t.devise || 'EUR') !== devise) return false;
      return true;
    });
  }, [items, type, devise]);

  // Stats by devise
  const byDevise = useMemo(() => {
    const map = {};
    items.forEach((t) => {
      const d = t.devise || 'EUR';
      if (!map[d]) map[d] = { count: 0, total: 0 };
      map[d].count += 1;
      map[d].total += Number(t.montant || 0);
    });
    return map;
  }, [items]);

  const swiftCount = items.filter((t) => (t.type_virement || 'sepa') === 'swift').length;
  const sepaCount = items.length - swiftCount;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{tt('admin.transfersTitle')}</h1>
          <p className="text-slate-500 text-sm">{tt('admin.transfersSubtitle')}</p>
        </div>
      </div>

      {/* Stats SEPA / SWIFT */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center"><Euro className="w-5 h-5"/></div>
          <div><div className="text-xs text-slate-500 uppercase font-medium">SEPA</div><div className="text-2xl font-bold">{sepaCount}</div></div>
        </div>
        <div className="stat-card flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center"><Globe2 className="w-5 h-5"/></div>
          <div><div className="text-xs text-slate-500 uppercase font-medium">SWIFT International</div><div className="text-2xl font-bold">{swiftCount}</div></div>
        </div>
        {Object.entries(byDevise).slice(0, 2).map(([d, s]) => (
          <div key={d} className="stat-card">
            <div className="text-xs text-slate-500 uppercase font-medium">{d}</div>
            <div className="text-xl font-bold">{s.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {d}</div>
            <div className="text-xs text-slate-500">{s.count} virement{s.count > 1 ? 's' : ''}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-slate-500 uppercase font-medium">Statut</label>
          <Select value={statut} onValueChange={setStatut}>
            <SelectTrigger data-testid="select-transfer-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="en_attente_validation">En attente</SelectItem>
              <SelectItem value="en_cours_traitement">En cours</SelectItem>
              <SelectItem value="valide">Validés</SelectItem>
              <SelectItem value="execute">Exécutés</SelectItem>
              <SelectItem value="annule_client">Annulé client</SelectItem>
              <SelectItem value="annule_admin">Annulé admin</SelectItem>
              <SelectItem value="bloque">Bloqué</SelectItem>
              <SelectItem value="suspendu">Suspendu</SelectItem>
              <SelectItem value="refuse">Refusé</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase font-medium">Type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="select-transfer-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              <SelectItem value="sepa">🇪🇺 SEPA (Zone euro)</SelectItem>
              <SelectItem value="swift">🌍 SWIFT International</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase font-medium">Devise</label>
          <Select value={devise} onValueChange={setDevise}>
            <SelectTrigger data-testid="select-transfer-devise"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes devises</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="CHF">CHF</SelectItem>
              <SelectItem value="CAD">CAD</SelectItem>
              <SelectItem value="JPY">JPY</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Client</th>
                <th className="p-3 text-left">Bénéficiaire</th>
                <th className="p-3 text-center">Type</th>
                <th className="p-3 text-right">Montant</th>
                <th className="p-3">Statut</th>
                <th className="p-3">Codes</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((t) => {
                const isSwift = (t.type_virement || 'sepa') === 'swift';
                const dev = t.devise || 'EUR';
                return (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="p-3 text-xs text-slate-600">{new Date(t.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="p-3 text-xs">{t.user_email}</td>
                    <td className="p-3">
                      <div className="font-medium">{t.beneficiaire_nom}</div>
                      <div className="text-xs text-slate-500 font-mono">{t.beneficiaire_iban}</div>
                      {isSwift && t.pays_beneficiaire && <div className="text-xs text-slate-400">{t.pays_beneficiaire}</div>}
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant={isSwift ? 'default' : 'secondary'} className={isSwift ? 'bg-purple-100 text-purple-800 hover:bg-purple-100' : ''}>
                        {isSwift ? '🌍 SWIFT' : '🇪🇺 SEPA'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right font-bold">
                      {Number(t.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs text-slate-500 font-medium">{dev}</span>
                    </td>
                    <td className="p-3 text-center text-xs"><Badge>{t.statut}</Badge></td>
                    <td className="p-3 text-center text-xs">{t.codes_valides}/{t.codes_total}</td>
                    <td className="p-3 text-right">
                      <Link to={`/admin/virements/${t.id}`}>
                        <Button size="sm" variant="outline" data-testid={`btn-view-transfer-${t.id}`}>Voir</Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">Aucun virement avec ces filtres</div>
        )}
      </div>
    </div>
  );
}
